import { Injectable, inject } from '@angular/core';
import {
  Router,
  NavigationEnd,
  NavigationStart,
  Params,
} from '@angular/router';
import { Subject, BehaviorSubject, filter } from 'rxjs';

export interface BreadcrumbReturnEvent {
  stateKey: string;
}

export const FROM_DASHBOARD_STATE_KEY = 'fromDashboard';

export const DASHBOARD_TYPE_STATE_KEY = 'dashboardType';

/** Dashboard search query params, so the Dashboard breadcrumb returns to the same search (SPEC-BKD-001 FR-BKD-009). */
export const DASHBOARD_QUERY_PARAMS_STATE_KEY = 'dashboardQueryParams';

interface PendingReturn {
  stateKey: string;
  detailUrlPattern: string;
  sourceUrl: string;  // URL when markNavigatingToDetail was called
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbNavigationService {
  private readonly router = inject(Router);
  private readonly pendingReturns = new Map<string, PendingReturn>();

  // Track the URL we're navigating FROM (captured at NavigationStart, before component init)
  private navigatingFromUrl = '';
  private currentUrl = '';

  /** Whether the current page was navigated to from Dashboard (show Dashboard link in breadcrumb) */
  private readonly fromDashboardSubject = new BehaviorSubject<boolean>(false);
  readonly fromDashboard$ = this.fromDashboardSubject.asObservable();

  private readonly returnDashboardTypeSubject = new BehaviorSubject<string | null>(null);
  readonly returnDashboardType$ = this.returnDashboardTypeSubject.asObservable();

  private readonly returnDashboardQueryParamsSubject =
    new BehaviorSubject<Params>({});
  readonly returnDashboardQueryParams$ =
    this.returnDashboardQueryParamsSubject.asObservable();

  private readonly breadcrumbReturn$ = new Subject<BreadcrumbReturnEvent>();

  /** Observable that emits when user returns via breadcrumb */
  readonly onBreadcrumbReturn$ = this.breadcrumbReturn$.asObservable();

  setFromDashboard(
    value: boolean,
    dashboardType?: string,
    queryParams?: Params
  ): void {
    this.fromDashboardSubject.next(value);
    this.returnDashboardTypeSubject.next(value && dashboardType ? dashboardType : null);
    const fallbackParams = dashboardType ? { dashboardType } : {};
    this.returnDashboardQueryParamsSubject.next(
      value ? queryParams ?? fallbackParams : {}
    );
  }

  getReturnDashboardType(): string | null {
    return this.returnDashboardTypeSubject.value;
  }

  constructor() {
    this.currentUrl = this.router.url;
    this.setupRouteMonitoring();
  }

  private setupRouteMonitoring(): void {
    // Capture the "from" URL at NavigationStart (before constructor runs)
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe(() => {
        this.navigatingFromUrl = this.currentUrl;
      });

    // Update currentUrl after navigation completes
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.currentUrl = event.urlAfterRedirects;
      });
  }

  /**
   * Call this when navigating FROM a summary page TO a detail page.
   * Marks the module as "pending return" so state will be restored on breadcrumb back.
   * @param stateKey - The state key for this module (e.g., 'airports')
   * @param detailUrlPattern - Optional URL pattern to identify detail page (default: derived from stateKey)
   */
  markNavigatingToDetail(stateKey: string, detailUrlPattern?: string): void {
    this.pendingReturns.set(stateKey, {
      stateKey,
      detailUrlPattern: detailUrlPattern ?? this.deriveDetailPattern(stateKey),
      sourceUrl: this.currentUrl,
    });
  }

  private deriveDetailPattern(stateKey: string): string {
    // 'airports' -> 'airport-information'
    const singular = stateKey.endsWith('s') ? stateKey.slice(0, -1) : stateKey;
    return `${singular}-information`;
  }

  /**
   * Check if this module should restore state (user is returning from detail page).
   * Only restores if:
   * 1. markNavigatingToDetail was called for this stateKey
   * 2. User is actually coming from the detail page URL
   * 
   * Automatically clears the flag after checking.
   */
  shouldRestoreState(stateKey: string): boolean {
    const pending = this.pendingReturns.get(stateKey);
    if (!pending) {
      return false;
    }

    // Clear the pending return regardless of whether we restore
    this.pendingReturns.delete(stateKey);

    // Check if we're coming from the detail page
    // navigatingFromUrl is set at NavigationStart, BEFORE component constructor runs
    const comingFromDetailPage = this.navigatingFromUrl.includes(pending.detailUrlPattern);
    if (comingFromDetailPage) {
      this.breadcrumbReturn$.next({ stateKey });
      return true;
    }

    // User came from somewhere else (sidebar, etc.) - don't restore
    return false;
  }

  /**
   * Clear pending return for a module (e.g., if user navigates elsewhere).
   */
  clearPendingReturn(stateKey: string): void {
    this.pendingReturns.delete(stateKey);
  }

  /**
   * Clear all pending returns (e.g., on logout).
   */
  clearAll(): void {
    this.pendingReturns.clear();
  }
}
